/* Load review interfaces and checkout offers only when their surfaces are needed. */
(() => {
  'use strict';
  if (window.ShopstaApps) return;
  const config = document.querySelector('[data-shopsta-app-loading]');
  const loads = new Map();
  function script(source) {
    if (!source) return Promise.reject(new Error('App script unavailable'));
    if (!loads.has(source)) {
      const promise = new Promise((resolve, reject) => {
        const node = document.createElement('script');
        const timeout = setTimeout(() => fail(), 6000);
        function fail() { clearTimeout(timeout); node.remove(); reject(new Error('App script unavailable')); }
        node.async = true;
        node.src = source;
        node.onload = () => { clearTimeout(timeout); resolve(); };
        node.onerror = fail;
        document.head.append(node);
      });
      loads.set(source, promise);
      promise.catch(() => { if (loads.get(source) === promise) loads.delete(source); });
    }
    return loads.get(source);
  }
  function honeycomb() {
    if (typeof window.conversionBearUpsell?.showFunnel === 'function') return Promise.resolve(window.conversionBearUpsell);
    return script(config?.dataset.honeycombSrc).then(() => {
      if (typeof window.conversionBearUpsell?.showFunnel !== 'function') throw new Error('Upsell app unavailable');
      return window.conversionBearUpsell;
    });
  }
  function loox() {
    if (window.LOOX?.widgetRunOnce) return Promise.resolve();
    const template = document.querySelector('[data-shopsta-loox-loader]');
    const source = template?.content.querySelector('script[src]')?.getAttribute('src');
    return script(source);
  }
  let firstScreen;
  function backgroundAllowed() {
    const connection = navigator.connection;
    return !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType);
  }
  function onProductIdle(task) {
    if (!firstScreen) firstScreen = new Promise(resolve => {
      const start = () => {
        const hero = document.querySelector('[data-shopsta-product-image]');
        const imageReady = !hero || hero.complete ? Promise.resolve() : new Promise(done => {
          const finish = () => {
            hero.removeEventListener('load', finish);
            hero.removeEventListener('error', finish);
            done();
          };
          hero.addEventListener('load', finish, { once: true });
          hero.addEventListener('error', finish, { once: true });
        });
        Promise.allSettled([imageReady, document.fonts?.ready]).then(() => {
          let observer;
          const finish = () => {
            clearTimeout(timeout);
            observer?.disconnect();
            requestAnimationFrame(() => requestAnimationFrame(resolve));
          };
          // Image download completion can precede its first paint substantially.
          // If it is not an LCP candidate (or the API is unavailable), allow the
          // initial rendering a short settling period before background work.
          const timeout = setTimeout(finish, 2000);
          if (!hero || !window.PerformanceObserver?.supportedEntryTypes?.includes('largest-contentful-paint')) return;
          try {
            observer = new PerformanceObserver(list => {
              if (list.getEntries().some(entry => entry.element === hero)) finish();
            });
            observer.observe({ type: 'largest-contentful-paint', buffered: true });
          } catch { observer?.disconnect(); }
        });
      };
      if (document.readyState === 'complete') start();
      else window.addEventListener('load', start, { once: true });
    });
    firstScreen.then(function schedule() {
      if (document.hidden) {
        const resume = () => {
          if (document.hidden) return;
          document.removeEventListener('visibilitychange', resume);
          schedule();
        };
        document.addEventListener('visibilitychange', resume);
        return;
      }
      const run = () => {
        if (document.hidden) schedule();
        else task();
      };
      if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 2000 });
      else setTimeout(run, 200);
    });
  }
  window.ShopstaApps = { honeycomb, loox, onProductIdle, backgroundAllowed };
  window.addEventListener('shopsta:cart-opening', () => { honeycomb().catch(() => {}); });
  // Also support the server-rendered fallback cart and locale-prefixed cart URLs.
  if (/\/cart\/?$/.test(location.pathname) || window.Shopify?.designMode) honeycomb().catch(() => {});

  const watched = new WeakSet();
  function ready(task) {
    if (document.readyState === 'complete') task();
    else window.addEventListener('load', () => {
      if (window.requestIdleCallback) window.requestIdleCallback(task, { timeout: 1500 });
      else setTimeout(task, 0);
    }, { once: true });
  }
  function reveal(section) {
    const template = section.querySelector('[data-shopsta-loox-content]');
    if (template) template.replaceWith(template.content.cloneNode(true));
    return loox();
  }
  const reviewsObserver = window.IntersectionObserver && new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) {
      reviewsObserver.unobserve(entry.target);
      reveal(entry.target).catch(() => {});
    }
  }, { rootMargin: '800px 0px' });
  const widgetObserver = window.IntersectionObserver && new IntersectionObserver(entries => {
    for (const entry of entries) if (entry.isIntersecting) {
      widgetObserver.unobserve(entry.target);
      ready(() => { loox().catch(() => {}); });
    }
  });
  const selector = '[data-shopsta-loox-section], .loox-widget, .loox-rating:not([data-shopsta-native-rating])';
  function watch(node) {
    if (node.nodeType !== 1 || watched.has(node)) return;
    watched.add(node);
    if (node.matches('[data-shopsta-native-rating]') || node.closest('.satcb_reviews_stars')) return;
    const reviews = node.matches('[data-shopsta-loox-section]');
    if (reviews && !node.hasAttribute('data-shopsta-loox-empty') && backgroundAllowed()) {
      onProductIdle(() => {
        if (node.isConnected && backgroundAllowed()) reveal(node).catch(() => {});
      });
    }
    const observer = reviews ? reviewsObserver : widgetObserver;
    if (observer && !window.Shopify?.designMode) observer.observe(node);
    else ready(() => { (reviews ? reveal(node) : loox()).catch(() => {}); });
  }
  function discover(root) {
    if (root.matches?.(selector)) watch(root);
    root.querySelectorAll?.(selector).forEach(watch);
  }
  discover(document);
  // Quick shop and theme-editor sections may introduce genuine Loox app widgets.
  // Native card stars need no observer or repeated client-side rendering.
  new MutationObserver(records => {
    for (const record of records) for (const node of record.addedNodes) {
      if (node.nodeType === 1) discover(node);
    }
  }).observe(document.body, { childList: true, subtree: true });

  function reviewTarget() {
    const section = document.querySelector('[data-shopsta-loox-section]');
    if (section) { reveal(section).catch(() => {}); return section; }
    const target = document.getElementById('looxReviews');
    if (target) loox().catch(() => {});
    return target;
  }
  function scrollToReviews(target) {
    if (!target) return;
    target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
    // Long descriptions can settle while the review iframe grows. Align once more
    // when reviews arrive, unless the shopper has started scrolling themselves.
    if (!window.ResizeObserver || target.querySelector('iframe')?.getBoundingClientRect().height > 100) return;
    const inputs = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    const observer = new ResizeObserver(() => {
      if (target.querySelector('iframe')?.getBoundingClientRect().height > 100) {
        stop();
        target.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    });
    const timeout = setTimeout(stop, 8000);
    function stop() {
      observer.disconnect();
      clearTimeout(timeout);
      for (const input of inputs) window.removeEventListener(input, stop);
    }
    for (const input of inputs) window.addEventListener(input, stop, { passive: true, once: true });
    observer.observe(target);
  }
  document.addEventListener('click', event => {
    const link = event.target.closest?.('a[href]');
    if (!link || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname || !['#looxReviews', '#looxReviewsFrame'].includes(url.hash)) return;
    const target = reviewTarget();
    if (!target) return;
    event.preventDefault();
    scrollToReviews(target);
  });
  // Keep review links from emails, review requests and shared URLs functional.
  function deepLink() {
    if (['#looxReviews', '#looxReviewsFrame'].includes(location.hash)) scrollToReviews(reviewTarget());
    if (/(?:[?&])(?:ref=(?:loox|review)|loox[^=]*=)/i.test(location.search)) {
      reviewTarget();
      loox().catch(() => {});
    }
  }
  window.addEventListener('hashchange', deepLink);
  deepLink();
})();
